import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import { ReportFilterState, toApiParams } from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.scss']
})
export class AnalyticsDashboardComponent implements OnInit, OnChanges {

  @Input() storeId: string = '';
  @Input() dashboardData: any = {};
  @Input() dashboardLoading: boolean = false;
  @Output() refreshRequested = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<ReportFilterState>();

  @ViewChild('trendChart') trendChart?: BaseChartDirective;
  @ViewChild('categoryChart') categoryChart?: BaseChartDirective;

  filterState: ReportFilterState = { period: 'this_month' };
  salesOverview: any = {};
  inventorySummary: any = {};
  currentBalance: any = {};
  topProducts: any[] = [];
  orderTrends: any[] = [];
  baBreakdown: any[] = [];
  comparison: any = { available: false };
  stockAlerts: any = { count: 0, items: [] };
  showAllStockAlerts: boolean = false;

  categoryLoading: boolean = false;
  private categoryLoadSeq = 0;

  public trendChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Revenue (DKK)',
        backgroundColor: 'rgba(51, 102, 255, 0.15)',
        borderColor: 'rgba(51, 102, 255, 1)',
        pointBackgroundColor: 'rgba(51, 102, 255, 1)',
        fill: true,
        yAxisID: 'y'
      },
      {
        data: [],
        label: 'Orders',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        borderColor: 'rgba(40, 167, 69, 1)',
        pointBackgroundColor: 'rgba(40, 167, 69, 1)',
        fill: false,
        yAxisID: 'y1'
      }
    ]
  };

  public trendChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Revenue' } },
      y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Orders' } }
    },
    plugins: { legend: { display: true } }
  };

  public trendChartType: ChartType = 'line';

  public categoryChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [{
      data: [],
      label: 'Revenue by BA',
      backgroundColor: [
        '#3366ff', '#00d68f', '#ffaa00', '#ff3d71', '#0095ff',
        '#8f5fe8', '#ffc94d', '#2ce69b', '#598bff', '#ff708d'
      ]
    }]
  };

  public categoryChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: { legend: { position: 'right' } }
  };

  public categoryChartType: ChartType = 'doughnut';

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    // Initial data load driven by filter bar emit + parent dashboard fetch
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardData'] && this.dashboardData) {
      this.processDashboardData();
    }
  }

  onFilterChange(filter: ReportFilterState): void {
    this.filterState = filter;
    this.showAllStockAlerts = false;
    this.filterChange.emit(filter);
    this.loadCategoryReport();
  }

  loadCategoryReport(): void {
    if (!this.storeId) return;
    const seq = ++this.categoryLoadSeq;
    this.categoryLoading = true;
    const params = toApiParams(this.filterState, { compare: 'true' });
    this.ecommerceService.getStoreCategoryReport(this.storeId, params).subscribe({
      next: (res: any) => {
        if (seq !== this.categoryLoadSeq) return;
        const data = this.unwrapResponse(res);
        this.baBreakdown = data?.baBreakdown || [];
        this.updateCategoryChart();
        this.categoryLoading = false;
      },
      error: (err: any) => {
        if (seq !== this.categoryLoadSeq) return;
        this.baBreakdown = [];
        this.updateCategoryChart();
        this.categoryLoading = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load category breakdown');
      }
    });
  }

  processDashboardData(): void {
    if (!this.dashboardData) return;

    this.salesOverview = this.dashboardData.salesOverview || {};
    this.inventorySummary = this.dashboardData.inventorySummary || {};
    this.currentBalance = this.dashboardData.currentBalance || {};
    this.topProducts = this.dashboardData.topProducts || [];
    this.orderTrends = this.dashboardData.orderTrends || this.dashboardData.dailyTrend || [];
    this.comparison = this.dashboardData.comparison || { available: false };
    this.stockAlerts = this.dashboardData.stockAlerts || { count: 0, items: [] };
    this.showAllStockAlerts = false;
    this.updateTrendChart();
  }

  updateTrendChart(): void {
    const labels: string[] = [];
    const revenues: number[] = [];
    const orders: number[] = [];

    this.orderTrends.forEach((trend: any) => {
      if (trend.date) {
        labels.push(trend.date);
        revenues.push(trend.revenue || 0);
        orders.push(trend.orders || 0);
      } else if (trend._id) {
        const d = trend._id;
        const label = d.day
          ? `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
          : d.week
            ? `${d.year}-W${d.week}`
            : `${d.year}-${String(d.month).padStart(2, '0')}`;
        labels.push(label);
        revenues.push(trend.revenue || 0);
        orders.push(trend.orders || 0);
      }
    });

    this.trendChartData.labels = labels;
    this.trendChartData.datasets[0].data = revenues;
    this.trendChartData.datasets[1].data = orders;
    this.trendChart?.update();
  }

  updateCategoryChart(): void {
    const topBas = [...this.baBreakdown]
      .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0))
      .slice(0, 8);

    this.categoryChartData.labels = topBas.map(ba => ba.baName || 'Unknown');
    this.categoryChartData.datasets[0].data = topBas.map(ba => ba.totalRevenue || 0);
    this.categoryChart?.update();
  }

  unwrapResponse(res: any): any {
    if (res?.response?.success && res.response.data !== undefined) return res.response.data;
    if (res?.code === 200 && res.data !== undefined) return res.data;
    return res;
  }

  formatCurrency(amount: number): string {
    if (amount == null || isNaN(amount)) {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(0);
    }
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(amount);
  }

  formatNumber(num: number): string {
    if (num == null || isNaN(num)) return '0';
    return new Intl.NumberFormat('da-DK').format(num);
  }

  formatDelta(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return '';
    const sign = deltaPct >= 0 ? '+' : '';
    return `${sign}${deltaPct.toFixed(1)}%`;
  }

  deltaClass(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return 'text-muted';
    if (deltaPct > 0) return 'text-success';
    if (deltaPct < 0) return 'text-danger';
    return 'text-muted';
  }

  refreshData(): void {
    this.refreshRequested.emit();
    this.loadCategoryReport();
  }

  get hasChartData(): boolean {
    return this.orderTrends.length > 0 || this.baBreakdown.length > 0;
  }

  get visibleStockAlerts(): any[] {
    const items = this.stockAlerts?.items || [];
    return this.showAllStockAlerts ? items : items.slice(0, 5);
  }

  stockAlertQuantity(item: any): number {
    if (item?.alertInfo?.currentStock != null) return item.alertInfo.currentStock;
    if (item?.inventory?.quantity != null) return item.inventory.quantity;
    if (item?.currentStock != null) return item.currentStock;
    if (item?.stock != null) return item.stock;
    return 0;
  }
}
